import LogRocket from "logrocket";
import moment from "moment-timezone";

import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { httpAuth2 } from "@/api/auth";
import { patientRequests } from "@/api/(requests)/patient";
import { scheduleRequests } from "@/api/(requests)/schedule";

import * as actionTypes from "./actionTypes";

const loadAssessment = (startQuestion, assessmentPerc) => {
  return {
    payload: { assessmentPerc, startQuestion },
    type: actionTypes.START_ASSESSMENT,
  };
};

const nextQuestion = (nextQuestion, isLast, assessmentPerc) => {
  return {
    payload: { assessmentPerc, isLast, nextQuestion },
    type: actionTypes.NEXT_QUESTION,
  };
};

const previousQuestion = (payload) => {
  return {
    payload,
    type: actionTypes.PREVIOUS_QUESTION,
  };
};

const selectOption = (options) => {
  return {
    payload: options,
    type: actionTypes.OPTION_SELECTED,
  };
};

const setAssessmentStatus = (status) => {
  return {
    payload: status,
    type: actionTypes.SET_ASSESSMENT_STATUS,
  };
};

export const startAssessment = (assessmentType, exceptionConfig) => (dispatch) => {
  let promise = null;

  if (assessmentType === "post_90_day") {
    promise = patientRequests.start_outcome_assessment();
  } else {
    let data = {};

    if (assessmentType != null && assessmentType !== "") {
      data["assessment_type"] = assessmentType;
    }

    promise = patientRequests.get_next_question({ context: { exceptionConfig }, data });
  }

  promise
    .then((cleanResponse) => {
      if (!cleanResponse) {
        dispatch({ type: actionTypes.NO_ASSESSMENT });
      } else {
        let question = cleanResponse.question;

        if (question == null) {
          dispatch({ type: actionTypes.START_RATING_SCALE });
        } else {
          let assessmentPerc = cleanResponse.percentage;

          dispatch(loadAssessment(cleanResponse.question, assessmentPerc));
        }
      }
    })
    // No toast here — known errors (e.g. NoActiveAssessmentError) are handled by
    // exceptionConfig callbacks wired in Assessment.js. This catch covers unexpected
    // failures (network, 5xx) that would otherwise be silent unhandled rejections.
    .catch((error) => {
      captureErrorMessage(parseError({ error, fallback: "Failed to start assessment" }), {
        tags: { component: "Assessment", errorType: "start_assessment" },
      });
    });
};

export const getNextQuestion = (requestBody, assessmentType, exceptionConfig) => (dispatch) => {
  let request =
    "post_90_day" !== assessmentType
      ? patientRequests.submit_question
      : patientRequests.fetch_next_outcome_question;

  request({ context: { exceptionConfig }, data: requestBody })
    .then((cleanResponse) => {
      if (cleanResponse === null) {
        dispatch({ type: actionTypes.ASSESSMENT_COMPLETED });
        console.error("Unexpected response from assessment submit_question");
        LogRocket.captureMessage("Unexpected response from assessment submit_question");

        return;
      }
      let nextQ = cleanResponse.question;
      let assessmentPerc = cleanResponse.percentage;
      let isLast = (cleanResponse.question && cleanResponse.question.is_last) || false;

      if (nextQ == null) {
        dispatch({ type: actionTypes.ASSESSMENT_COMPLETED });
      } else {
        dispatch(nextQuestion(nextQ, isLast, assessmentPerc));
      }
    })
    // Same rationale as startAssessment: exceptionConfig handles known server errors;
    // this catch is for unexpected rejections only.
    .catch((error) => {
      captureErrorMessage(
        parseError({ error, fallback: "Failed to get next assessment question" }),
        {
          tags: { component: "Assessment", errorType: "get_next_question" },
        },
      );
    });
};

export const getPreviousQuestion = (assessmentType, exceptionConfig) => (dispatch) => {
  let promise = null;

  if (assessmentType === "post_90_day") {
    promise = patientRequests.fetch_previous_outcome_question();
  } else {
    let data = {};

    if (assessmentType != null && assessmentType !== "") {
      data["assessment_type"] = assessmentType;
    }

    promise = patientRequests.get_last_question({
      context: { exceptionConfig },
      data,
    });
  }

  promise
    .then((cleanResponse) => {
      // Null guard added: API returning null/undefined would have thrown at
      // cleanResponse.percentage (uncaught). Silent return matches original
      // intent — no Redux state change, no user-visible feedback.
      if (!cleanResponse) return;

      let assessmentPerc = cleanResponse.percentage;
      let payload = {
        assessmentPerc,
        currentQuestion: cleanResponse.question,
      };

      dispatch(previousQuestion(payload));
    })
    // Same rationale as startAssessment: exceptionConfig handles known server errors;
    // this catch is for unexpected rejections only.
    .catch((error) => {
      captureErrorMessage(
        parseError({ error, fallback: "Failed to get previous assessment question" }),
        {
          tags: { component: "Assessment", errorType: "get_previous_question" },
        },
      );
    });
};

export const getAssessmentStatus = () => (dispatch) => {
  patientRequests
    .fetch_assessment_status({ context: { showLoader: false } })
    .then((cleanResponse) => {
      let status = cleanResponse.user;

      dispatch(setAssessmentStatus(status));
    });
};

export const setSelectedOptions = (selectedOptions, selectedOptionFull) => (dispatch) => {
  dispatch(selectOption({ selectedOptionFull, selectedOptions }));
};

export const submitRatingScaleQuestions = (data) => (dispatch) => {
  return patientRequests.submit_rating_scale_questions({ data }).then(() => {
    dispatch({ type: actionTypes.ASSESSMENT_COMPLETED });
  });
};

export const updateScheduleList = () => (dispatch) => {
  scheduleRequests
    .get_schedule({
      context: { showLoader: false },
      searchParams: {
        local_start_date_time: moment().startOf("day").format("YYYY-MM-DD HH:mm"),
        timezone: moment.tz.guess(),
      },
    })
    .then((response) => {
      dispatch({
        payload: {
          scheduleList: response.schedule || [],
        },
        type: actionTypes.UPDATE_SCHEDULE_LIST,
      });
    })
    .catch(() => {
      dispatch({
        payload: {
          scheduleList: [],
        },
        type: actionTypes.UPDATE_SCHEDULE_LIST,
      });
    });
};

export const updateCrisisRisk = (data) => {
  return {
    payload: {
      crisisRisk: data,
    },
    type: actionTypes.UPDATE_CRISIS_RISK,
  };
};

export const allowLoginPopUps = () => {
  const payload = {
    allowInactivePopUp: true,
    allowWaitListPopUp: true,
  };

  return {
    payload,
    type: actionTypes.ALLOW_LOGIN_POP_UPS,
  };
};

export const disableLoginPopUps = () => {
  return {
    type: actionTypes.DISABLE_LOGIN_POP_UPS,
  };
};

export const waitlistStatusChanged = () => ({ type: actionTypes.WAITLIST_STATUS_CHANGED });

// ----------- Thunks --------------

export const fetchAssessmentCrisisRisk = async ({ context, dispatch, searchParams } = {}) => {
  return httpAuth2
    .post("/v1/a5_screening/get_crisis_risk", {
      context,
      ...(searchParams && { searchParams }),
    })
    .json()
    .then((data) => {
      dispatch(updateCrisisRisk(data));

      return data;
    });
};
